import { Transaction, getInputType, NETWORK, TEST_NETWORK, Address } from '@scure/btc-signer';
import { base64, hex } from '@scure/base';
import { bech32, bech32m } from '@scure/base';

/**
 * Granular breakdown of a transaction's structural payload.
 * Provides a human-readable summary of inputs, outputs, and network fees.
 */
export interface TxDetails {
  /** The unique transaction identifier (hash). */
  txId: string;
  /** The total outbound volume of the transaction, expressed in satoshis (excluding fees). */
  amount: number;
  /** The total network fee calculated as the difference between total inputs and outputs. */
  fee: number;
  /** The estimated virtual byte (vB) size of the finalized transaction. */
  vBytes: number;
  /** The effective fee rate paid to miners, expressed in satoshis per vByte (sat/vB). */
  feeRate: number;
  /** The total count of unspent transaction outputs (UTXOs) consumed as inputs. */
  inputs: number;
  /** absolute locktime (nLockTime) */
  lockTime: number; 
  /** whether any input has a BIP-68 relative lock */
  hasRelativeTimelock: boolean;
  /** human-readable string of the strictest relative lock(s) */
  relativeTimelockValue: string | null;
  /** Detailed mapping of every consumed input, including origin txId and satoshi value. */
  inputsList: { address: string; amount: number; txId: string; vout: number; sequence: number, isRbfEnabled:boolean, scriptType: string; }[];
  /** Detailed mapping of every created output, indicating destination addresses and change routing. */
  outputs: { address: string; amount: number; isChange: boolean, scriptType: string; }[];
  /** If any input has Replace-By-Fee (BIP-125) enabled for transaction*/
  isRbfEnabled: boolean;
}

/**
 * Represents the signature status of a specific hardware wallet or signing participant.
 */
export interface SignerStatus {
  /** The master key fingerprint (8 hex characters) identifying the signer. */
  fingerprint: string;
  /** Indicates whether a valid partial signature exists from this participant. */
  signed: boolean;
}

/**
 * High-level analytical overview of a Partially Signed Bitcoin Transaction (PSBT).
 * Used for rapid validation and environmental routing.
 */
export interface PsbtAnalysis {
  /** Indicates if the provided PSBT string is structurally sound and parsable. */
  valid: boolean;
  /** The total count of distinct extended public key fingerprints involved in the transaction. */
  signerCount: number;
  /** The aggregate transaction output volume converted to whole Bitcoin (BTC). */
  amountBtc: number;
  /** The absolute network fee expected by the network, expressed in satoshis. */
  networkFeeSat: number;
  /** The total count of input UTXOs resulting from this transaction. */
  inputCount: number; 
  /** The total count of newly created UTXOs resulting from this transaction. */
  outputCount: number;
  /** The estimated virtual byte (vB) size of the transaction based on input and output types. */
  estimatedVBytes: number;
  /** The cryptographic network environment derived from BIP32 derivation paths. */
  detectedNetwork: 'bitcoin' | 'testnet' | 'unknown';
}

/**
 * Utility class providing static methods for parsing, validating, mutating,
 * and extracting metadata from Partially Signed Bitcoin Transactions (PSBTs).
 */
export class PsbtUtils {
  /**
   * Decodes a raw PSBT string payload into a strictly typed binary byte array.
   * Intelligently detects and routes between hex-encoded and base64-encoded formats.
   * 
   * @param raw - The raw, unformatted PSBT string payload.
   * @returns A decoded Uint8Array containing the raw binary transaction.
   */
  static decode(raw: string): Uint8Array {
    const clean = raw.replace(/\s/g, '');
    return /^[0-9a-fA-F]+$/.test(clean) ? hex.decode(clean) : base64.decode(clean);
  }

  /**
   * Normalizes an arbitrary PSBT input string into a standard Base64 representation.
   * Auto-converts valid hex strings containing the PSBT magic bytes into Base64.
   * 
   * @param input - The raw PSBT string (Base64 or Hex).
   * @returns A sanitized, Base64 encoded PSBT string.
   */
  static normalize(input: string): string {
    const clean = input.trim();
    if (/^[0-9a-fA-F]+$/.test(clean) && clean.toLowerCase().startsWith('70736274')) {
      try {
        return base64.encode(hex.decode(clean));
      } catch (e) {
        return input;
      }
    }
    return clean;
  }

  /**
   * Cryptographically merges two distinct PSBT payloads sharing the same underlying transaction state.
   * Used to aggregate isolated participant signatures into a unified transaction map.
   * 
   * @param base - The primary Base64 PSBT payload.
   * @param next - The secondary Base64 PSBT payload containing parallel signatures.
   * @returns A new Base64 PSBT string containing the combined signatures, or the original base on failure.
   */
  static merge(base: string, next: string): string {
    try {
      const txBase = Transaction.fromPSBT(this.decode(base));
      const txNext = Transaction.fromPSBT(this.decode(next));
      txBase.combine(txNext);
      return base64.encode(txBase.toPSBT());
    } catch (e) {
      console.error('[Merge Failed]', e);
      return base;
    }
  }

  /**
   * Extracts the required signature threshold for the PSBT UI.
   * Calculates the MAXIMUM threshold required by any single input, accurately reflecting 
   * the minimum number of physical device uploads required.
   * 
   * @param psbtBase64 - The normalized Base64 PSBT payload.
   * @returns The integer representing the required device uploads.
   */
  static getThreshold(psbtBase64: string): number {
    try {
      const tx = Transaction.fromPSBT(base64.decode(psbtBase64));

      let maxThreshold = 0;
      let unknownInputs = 0;

      for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);
        if (!input) continue;

        let inputThreshold = 0;

        // 1. Legacy SegWit / P2SH multisig
        const legacyScript = input.witnessScript || input.redeemScript;
        if (legacyScript && legacyScript.length > 0) {
          inputThreshold = this.extractThresholdFromScript(legacyScript);
        }

        // 2. Taproot script-path – take the highest threshold among all leaves
        if (input.tapLeafScript && input.tapLeafScript.length > 0) {
          let highestLeaf = 0;

          for (const leaf of input.tapLeafScript) {
            // leaf is typically [controlBlock, scriptWithVersion]
            // scriptWithVersion = script bytes + 1-byte leaf version at the end
            const rawValue = (leaf as any)[1] as Uint8Array | undefined;
            if (!rawValue || rawValue.length < 2) continue;

            const script = rawValue.slice(0, -1);
            const leafReq = this.extractThresholdFromScript(script);

            if (leafReq > highestLeaf) highestLeaf = leafReq;
          }

          if (highestLeaf > inputThreshold) inputThreshold = highestLeaf;
        }

        // 3. Pure Taproot key-path (no leaves)
        if (
          inputThreshold === 0 &&
          (!input.tapLeafScript || input.tapLeafScript.length === 0) &&
          input.tapBip32Derivation &&
          input.tapBip32Derivation.length > 0
        ) {
          inputThreshold = 1;
        }

        if (inputThreshold > 0) {
          if (inputThreshold > maxThreshold) maxThreshold = inputThreshold;
        } else {
          unknownInputs++;
        }
      }

      if (maxThreshold === 0 && unknownInputs > 0) return 0;
      return maxThreshold;
    } catch (e) {
      console.error('[PsbtUtils] Error extracting threshold:', e);
      return 0;
    }
  }

  /**
   * Identifies the primary signer's master key fingerprint associated with the first detected partial signature.
   * Works across Legacy, Segwit, and Taproot.
   * 
   * @param psbtData - The normalized Base64 PSBT payload.
   * @returns The 8-character hex fingerprint string, or null if no valid signature mapping is found.
   */
  static getFingerprintFromPsbt(psbtData: string): string | null {
    try {
      const bytes = this.decode(psbtData);
      const tx = Transaction.fromPSBT(bytes);

      for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);

        // 1. Legacy ECDSA Signatures
        if (input.partialSig && input.partialSig.length > 0) {
          const pubkeySigned = input.partialSig[0][0];
          if (input.bip32Derivation) {
            for (const [pubkey, meta] of input.bip32Derivation) {
              if (this.areKeysEqual(pubkey, pubkeySigned)) {
                return meta.fingerprint.toString(16).padStart(8, '0');
              }
            }
          }
        }

        // 2. Taproot Script Path Signatures
        if (input.tapScriptSig && input.tapScriptSig.length > 0) {
          const sigEntry = input.tapScriptSig[0] as any;
          const pubkeySigned = sigEntry.pubkey || (sigEntry[0] && sigEntry[0].pubKey);

          if (pubkeySigned && input.tapBip32Derivation) {
            for (const [pubkey, meta] of input.tapBip32Derivation) {
              if (this.areKeysEqual(pubkey, pubkeySigned)) {
                const derInfo = (meta as any).der || meta;
                const fp = derInfo?.fingerprint ?? derInfo?.masterFingerprint;
                if (fp != null) {
                  return fp.toString(16).padStart(8, '0');
                }
              }
            }
          }
        }

        // 3. Taproot Keypath Signatures
        if (input.tapKeySig && input.tapInternalKey && input.tapBip32Derivation) {
          for (const [pubkey, meta] of input.tapBip32Derivation) {
            if (this.areKeysEqual(pubkey, input.tapInternalKey)) {
              const derInfo = (meta as any).der || meta;
              const fp = derInfo?.fingerprint ?? derInfo?.masterFingerprint;
              if (fp != null) {
                return fp.toString(16).padStart(8, '0');
              }
            }
          }
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Scans a PSBT to map all expected participants and evaluates their current signature state.
   * Works across legacy ECDSA (partialSig), Schnorr (tapScriptSig), and Keypath (tapKeySig).
   * 
   * @param psbtBase64 - The normalized Base64 PSBT payload.
   * @returns An array mapping participant fingerprints to their active signed status.
   */
  static extractSigners(psbtBase64: string): SignerStatus[] {
    try {
      const tx = Transaction.fromPSBT(base64.decode(psbtBase64));
      const signersMap = new Map<string, boolean>();

      for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);

        // Helper to evaluate signatures for a given pubkey and fingerprint
        const processDerivation = (pubkey: Uint8Array, fp: any) => {
          if (fp == null) return;
          const fpHex = fp.toString(16).padStart(8, '0');
          let isSigned = false;

          // Check Legacy ECDSA
          if (input.partialSig) {
            isSigned = input.partialSig.some((p) => this.areKeysEqual(p[0], pubkey));
          }
          
          // Check Tapscript Schnorr (Leaf Spend)
          if (!isSigned && input.tapScriptSig) {
            isSigned = input.tapScriptSig.some((p: any) => {
              const pKey = p.pubkey || (p[0] && p[0].pubKey);
              return pKey ? this.areKeysEqual(pKey, pubkey) : false;
            });
          }
          
          // Check Taproot Keypath (ONLY mark true if the derivation matches the internal key)
          if (!isSigned && input.tapKeySig && input.tapInternalKey) {
            if (this.areKeysEqual(input.tapInternalKey, pubkey)) {
              isSigned = true;
            }
          }

          const current = signersMap.get(fpHex) || false;
          signersMap.set(fpHex, current || isSigned);
        };

        // Process Legacy SegWit Derivations
        if (input.bip32Derivation) {
          for (const [pubkey, meta] of input.bip32Derivation) {
            processDerivation(pubkey, meta?.fingerprint);
          }
        }

        // Process Taproot Derivations
        if (input.tapBip32Derivation) {
          for (const [pubkey, meta] of input.tapBip32Derivation) {
            const derInfo = (meta as any).der || meta;
            processDerivation(pubkey, derInfo?.fingerprint ?? derInfo?.masterFingerprint);
          }
        }
      }
      return Array.from(signersMap.entries()).map(([fp, signed]) => ({ fingerprint: fp, signed }));
    } catch (e) {
      return [];
    }
  }

  /**
   * Decodes an arbitrary output script into a human-readable Bitcoin address.
   * Supports P2PKH, P2SH, SegWit v0 (Bech32), and Taproot (Bech32m).
   * 
   * @param script - The raw locking script byte array.
   * @param network - The target network configuration.
   * @returns The formatted public address, or a raw hex fallback if unsupported.
   */
  static formatScriptAddress(
    script: Uint8Array,
    network: 'bitcoin' | 'testnet' | 'signet',
  ): string {
    if (!script || script.length === 0) {
      return 'Unknown';
    }

    try {
      const s = hex.encode(script);
      const currentNetwork = network || 'bitcoin';
      const isTestnetLike = currentNetwork === 'testnet' || currentNetwork === 'signet';
      const hrp = isTestnetLike ? 'tb' : 'bc';
      const networkConfig = isTestnetLike ? TEST_NETWORK : NETWORK;

      if (s.startsWith('76a914') && s.endsWith('88ac') && script.length === 25) {
        const pubKeyHash = script.slice(3, 23);
        return Address(networkConfig).encode({ type: 'pkh', hash: pubKeyHash } as any);
      }

      if (s.startsWith('a914') && s.endsWith('87') && script.length === 23) {
        const scriptHash = script.slice(2, 22);
        return Address(networkConfig).encode({ type: 'sh', hash: scriptHash } as any);
      }

      if (
        (s.startsWith('0014') && script.length === 22) ||
        (s.startsWith('0020') && script.length === 34)
      ) {
        const witnessProgram = script.slice(2);
        const words = bech32.toWords(witnessProgram);
        words.unshift(0);
        return bech32.encode(hrp, words);
      }

      if (s.length >= 4) {
        const versionByte = parseInt(s.slice(0, 2), 16);
        if (versionByte >= 0x51 && versionByte <= 0x60) {
          const witnessVersion = versionByte - 0x50;
          const witnessProgram = script.slice(2);
          const words = bech32m.toWords(witnessProgram);
          words.unshift(witnessVersion);
          return bech32m.encode(hrp, words);
        }
      }

      return s;
    } catch (e) {
      console.warn('Address formatting failed:', e, hex.encode(script));
      const s = hex.encode(script);
      if (s.startsWith('0014') || s.startsWith('0020') || s.startsWith('51')) return s.slice(4);
      if (s.startsWith('76a914')) return s.slice(6, 46);
      if (s.startsWith('a914')) return s.slice(4, 44);
      return s;
    }
  }

  /**
   * Validates structural equivalence between two public keys, handling compression mismatch safely.
   */
  private static areKeysEqual(k1: Uint8Array, k2: Uint8Array): boolean {
    if (hex.encode(k1) === hex.encode(k2)) return true;
    try {
      const getX = (k: Uint8Array) =>
        k.length === 33 ? k.slice(1) : k.length === 65 ? k.slice(1, 33) : k;
      return hex.encode(getX(k1)) === hex.encode(getX(k2));
    } catch (e) {
      return false;
    }
  }

  /**
   * Heuristically extracts an M-of-N threshold from a Bitcoin script.
   * Looks for OP_n CHECKMULTISIG / CHECKSIG patterns.
   * Returns 0 when no clear threshold can be determined.
   */
  private static extractThresholdFromScript(script: Uint8Array): number {
    if (!script || script.length === 0) return 0;

    // --- Tapscript multi_a (BIP 342) ---
    // Structure: <pk1> OP_CHECKSIG <pk2> OP_CHECKSIGADD ... <k> OP_NUMEQUAL (or OP_EQUAL)
    if (script.length >= 2) {
      const lastByte = script[script.length - 1];
      const preLastByte = script[script.length - 2];

      if (lastByte === 0x9c || lastByte === 0x87) {
        if (preLastByte >= 0x51 && preLastByte <= 0x60) {
          return preLastByte - 0x50; // OP_1 (0x51) -> 1, OP_2 (0x52) -> 2, etc.
        }
      }
    }

    // --- Classic Multisig (Legacy P2SH / SegWit P2WSH) ---
    // Structure: OP_m <pubkeys...> OP_n OP_CHECKMULTISIG(VERIFY)
    const firstOp = script[0];
    const lastOp = script[script.length - 1];
    if (lastOp === 0xae || lastOp === 0xaf) { // OP_CHECKMULTISIG / OP_CHECKMULTISIGVERIFY
      if (firstOp >= 0x51 && firstOp <= 0x60) {
        return firstOp - 0x50;
      }
    }

    // --- Single-Key Fallback ---
    // Tapscript single-sig ends with OP_CHECKSIG (0xac) or OP_CHECKSIGVERIFY (0xad)
    if (script[script.length - 1] === 0xac || script[script.length - 1] === 0xad) {
      return 1;
    }

    // If OP_CHECKSIG exists anywhere in a non-multisig script
    for (let i = 0; i < script.length; i++) {
      if (script[i] === 0xac || script[i] === 0xad) {
        return 1;
      }
    }

    return 0;
  }

  /**
   * Finalizes an fully-signed PSBT map into an extracted, broadcast-ready raw hex string.
   * 
   * @param psbtBase64 - The fully signed Base64 PSBT payload.
   * @returns An object containing the raw transaction hex and derived txId, or null on failure.
   */
  static finalizeTx(psbtBase64: string): { hex: string; txId: string } | null {
    try {
      const tx = Transaction.fromPSBT(this.decode(psbtBase64));
      tx.finalize();
      return { hex: hex.encode(tx.extract()), txId: tx.id };
    } catch (e) {
      return null;
    }
  }

  /**
   * Performs a rapid, unauthenticated surface analysis of a PSBT.
   * Primarily utilized to ascertain high-level sanity constraints and block out-of-network payloads.
   * 
   * @param psbtBase64 - The normalized Base64 PSBT payload.
   * @returns A high-level PsbtAnalysis configuration object, or null on parse failure.
   */
  static analyze(psbtBase64: string): PsbtAnalysis | null {
    try {
      const psbtBytes = this.decode(psbtBase64);
      const tx = Transaction.fromPSBT(psbtBytes, {
        allowUnknownInputs: true,
        allowUnknownOutputs: true,
      });

      const fingerprints = new Set<string>();
      let totalInput = BigInt(0);
      let totalOutput = BigInt(0);
      let networkScore = 0;
      let estimatedVBytes = 10;

      for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);
        const prev = this.getPrevOut(input);
        
        if (prev) {
          totalInput += prev.amount;
        }
        estimatedVBytes += this.estimateInputVBytes(input);

        // Process Legacy SegWit Derivations
        if (input.bip32Derivation) {
          for (const [, meta] of input.bip32Derivation) {
            if (meta?.fingerprint != null) {
              fingerprints.add(meta.fingerprint.toString(16).padStart(8, '0'));
            }
            if (meta?.path?.length >= 2) {
              const coinType = meta.path[1];
              if (coinType === 2147483648) networkScore--;
              if (coinType === 2147483649) networkScore++;
            }
          }
        }

        // Process Taproot Derivations
        if (input.tapBip32Derivation) {
          for (const [pubkey, meta] of input.tapBip32Derivation) {
            const derInfo = (meta as any).der || meta;
            const fp = derInfo?.fingerprint ?? derInfo?.masterFingerprint;
            const path = derInfo?.path;

            if (fp != null) {
              fingerprints.add(fp.toString(16).padStart(8, '0'));
            }
            if (path?.length >= 2) {
              const coinType = path[1];
              if (coinType === 2147483648) networkScore--;
              if (coinType === 2147483649) networkScore++;
            }
          }
        }
      }

      for (let i = 0; i < tx.outputsLength; i++) {
        const out = tx.getOutput(i);
        totalOutput += out.amount ?? BigInt(0);
        // Calculate exact output script length
        const scriptLen = out.script ? out.script.length : 22;
        estimatedVBytes += 9 + scriptLen; // 8-byte value + 1-byte length prefix + script
      }

      let fee = BigInt(0);
      try {
        fee = tx.fee;
      } catch {
        fee = totalInput > totalOutput ? totalInput - totalOutput : BigInt(0);
      }

      let vBytes = estimatedVBytes;
      try {
        const real = tx.vsize;
        if (real > 0) vBytes = real;
      } catch {}

      return {
        valid: true,
        signerCount: fingerprints.size || 1,
        amountBtc: Number(totalOutput) / 100000000,
        networkFeeSat: Number(fee),
        inputCount: tx.inputsLength,
        outputCount: tx.outputsLength,
        estimatedVBytes: vBytes,
        detectedNetwork: networkScore > 0 ? 'testnet' : 'bitcoin',
      };
    } catch (e) {
      console.error('[PsbtUtils] Analysis failed:', e);
      return null;
    }
  }

  /**
   * Completely parses a PSBT to derive human-readable financial metrics, I/O maps, and fee estimators.
   * 
   * @param psbtBase64 - The normalized Base64 PSBT payload.
   * @param network - The target network context required for accurate address formatting.
   * @returns A structured TxDetails object, or null if the parsing engine encounters a fatal error.
   */
  static parseTxDetails(
    psbtBase64: string,
    network: 'bitcoin' | 'testnet' | 'signet' = 'bitcoin',
  ): TxDetails | null {
    try {
      const tx = Transaction.fromPSBT(this.decode(psbtBase64), {
        allowUnknownInputs: true,
        allowUnknownOutputs: true,
      });

      const inputsList = [];
      const outputs = [];
      let totalInput = BigInt(0);
      let totalOutput = BigInt(0);
      let estimatedVBytes = 10;
      let hasRelativeTimelock = false;
      let isRbfEnabled = false;
      let maxBlocks = 0;
      let maxSeconds = 0;

      for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);
        let amount = BigInt(0);
        let address = 'Unknown';
        let txId = '????';
        let vout = input.index ?? 0;
        let scriptType = 'UNKNOWN';

        const sequence = input.sequence ?? 0xffffffff;

        // BIP-125: Check if any input signals Replace-By-Fee
        if (sequence < 0xfffffffe) {
          isRbfEnabled = true;
        }

        // Determine Input Script Type based on PSBT fields and address prefix
        if (input.tapInternalKey || input.tapLeafScript || input.tapBip32Derivation) {
          scriptType = 'P2TR';
        } else if (input.witnessScript) {
          scriptType = 'P2WSH';
        } else if (address.startsWith('bc1q') || address.startsWith('tb1q')) {
          scriptType = 'P2WPKH';
        } else if (address.startsWith('3') || address.startsWith('2')) {
          scriptType = 'P2SH';
        } else {
          scriptType = 'P2PKH';
        }

        // BIP 68: If the 31st bit is NOT set, a relative timelock is active.
        if ((sequence & 0x80000000) === 0) {
            hasRelativeTimelock = true;

            const isTimeBased = (sequence & 0x400000) !== 0;
            const value = sequence & 0xffff;
            
            if (isTimeBased) {
                const seconds = value * 512;
                if (seconds > maxSeconds) maxSeconds = seconds;
            } else {
                if (value > maxBlocks) maxBlocks = value;
            }
        }

        if (input.txid) {
          const full = hex.encode(input.txid);
          txId = full.slice(0, 8) + '...';
        }

        const prev = this.getPrevOut(input);
        if (prev) {
          amount = prev.amount;
          totalInput += amount;
          address = this.formatScriptAddress(prev.script, network);
        } else if (input.nonWitnessUtxo) {
          address = 'Legacy Input (unparsed)';
        }

        estimatedVBytes += this.estimateInputVBytes(input);
        inputsList.push({ address, amount: Number(amount), txId, vout, sequence, isRbfEnabled: sequence < 0xfffffffe, scriptType });
      }

      for (let i = 0; i < tx.outputsLength; i++) {
        const output = tx.getOutput(i);
        const amount = output.amount ?? BigInt(0);
        totalOutput += amount;
        
        const scriptLen = output.script ? output.script.length : 22;
        estimatedVBytes += 9 + scriptLen;

        const address = this.formatScriptAddress(output.script ?? new Uint8Array([]), network);
        let isChange = false;
        let scriptType = 'UNKNOWN';

        if (address.startsWith('bc1p') || address.startsWith('tb1p')) {
          scriptType = 'P2TR';
        } else if (address.startsWith('bc1q') || address.startsWith('tb1q')) {
          scriptType = address.length > 42 ? 'P2WSH' : 'P2WPKH';
        } else if (address.startsWith('3') || address.startsWith('2')) {
          scriptType = 'P2SH';
        } else {
          scriptType = 'P2PKH';
        }

        // Check Legacy & SegWit Outputs
        if (output.bip32Derivation) {
            isChange = output.bip32Derivation.some(([_, meta]) => {
                const path = meta.path || [];
                return path.length >= 2 && (path[path.length - 2] === (2147483648 + 1) || path[path.length - 2] === 1);
            });
        }

        // Check Taproot Outputs
        if (output.tapBip32Derivation) {
            isChange = output.tapBip32Derivation.some(([_, meta]) => {
                const path = meta.der?.path || [];
                return path.length >= 2 && (path[path.length - 2] === (2147483648 + 1) || path[path.length - 2] === 1);
            });
        }

        outputs.push({ address, amount: Number(amount), isChange, scriptType });
      }

      outputs.sort((a, b) => Number(b.isChange) - Number(a.isChange));

      let fee = BigInt(0);
      try {
        fee = tx.fee;
      } catch {
        fee = totalInput > totalOutput ? totalInput - totalOutput : BigInt(0);
      }

      let vBytes = estimatedVBytes;
      try {
        const real = tx.vsize;
        if (real > 0) vBytes = real;
      } catch {}

      const feeRate = vBytes > 0 ? Number((Number(fee) / vBytes).toFixed(2)) : 0;

      let relativeValueStr = null;
      if (hasRelativeTimelock) {
          if (maxBlocks > 0) relativeValueStr = `${maxBlocks} Blocks`;
          if (maxSeconds > 0) {
              const days = (maxSeconds / 86400).toFixed(1);
              relativeValueStr = relativeValueStr ? `${relativeValueStr} and ≈${days} Days` : `≈${days} Days`;
          }
      }

      return {
        txId: tx.id,
        amount: Number(totalOutput),
        fee: Number(fee),
        vBytes,
        feeRate,
        lockTime: tx.lockTime || 0,
        hasRelativeTimelock,
        relativeTimelockValue: relativeValueStr,
        inputs: tx.inputsLength,
        inputsList,
        outputs,
        isRbfEnabled,
      };
    } catch (e) {
      console.error('Failed to parse PSBT', e);
      return null;
    }
  }

  /**
   * Safely extracts the previous output amount and script from an input's UTXO data.
   */
  private static getPrevOut(
    input: ReturnType<Transaction['getInput']>
  ): { amount: bigint; script: Uint8Array } | null {
    if (input.witnessUtxo?.amount !== undefined) {
      return {
        amount: input.witnessUtxo.amount,
        script: input.witnessUtxo.script ?? new Uint8Array(),
      };
    }
    
    if (input.nonWitnessUtxo != null && input.index !== undefined) {
      try {
        const outputs = (input.nonWitnessUtxo as any).outputs;
        if (outputs && outputs[input.index]) {
          const out = outputs[input.index];
          if (out.amount == null) return null;
          return {
            amount: out.amount,
            script: out.script ?? new Uint8Array(),
          };
        }
        return null;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Heuristically estimates the virtual byte (vB) size of a given transaction input.
   * Handles key-path, classic multisig, and Taproot script-path (multi_a / CHECKSIGADD) dynamically.
   */
  private static estimateInputVBytes(
    input: ReturnType<Transaction['getInput']>
  ): number {
    try {
      const { type } = getInputType(input);

      if (type === 'wpkh' || type.startsWith('wpkh')) return 68;
      if (type === 'tr' || type.startsWith('tr')) {
        // Pure key-path (no leaves) → 58 vB
        if (!input.tapLeafScript || input.tapLeafScript.length === 0) {
          return 58;
        }
        // Fall through to dynamic Taproot script-path calculation below
      }
      if (type.includes('sh-wpkh')) return 91;
      if (type === 'pkh' || type === 'sh' || type.startsWith('sh-')) return 148;
    } catch {}

    // Classic Multisig / P2WSH (witnessScript)
    if (input.witnessScript && input.witnessScript.length > 0) {
      const m = this.extractThresholdFromScript(input.witnessScript);
      if (m > 0) {
        // Base: 41 bytes + witness (dummy + M×~73 + script)
        const witnessSize = 1 + (m * 73) + input.witnessScript.length;
        return Math.ceil(41 + (witnessSize / 4));
      }
      return Math.ceil(41 + (input.witnessScript.length / 4));
    }

    // Taproot script-path (tapLeafScript)
    if (input.tapLeafScript && input.tapLeafScript.length > 0) {
      let maxLeafVBytes = 0;

      for (const leaf of input.tapLeafScript) {
        // Correct structure from @scure/btc-signer:
        // leaf = [ {version, internalKey, merklePath}, scriptWithVersion ]
        const controlObj = (leaf as any)[0];
        const scriptWithVersion = (leaf as any)[1] as Uint8Array | undefined;

        if (!scriptWithVersion || scriptWithVersion.length < 2) continue;

        const script = scriptWithVersion.slice(0, -1); // strip leaf version byte
        const m = this.extractThresholdFromScript(script);

        // Reconstruct real control-block length
        // 1 byte (leaf version + parity) + 32 (internal key) + 32 × path length
        const pathLen = controlObj?.merklePath?.length ?? 0;
        const controlBlockLen = 1 + 32 + (pathLen * 32);

        // Witness stack: M × 64-byte Schnorr sigs + script + control block
        // + 1-byte compact-size length prefix per item
        const sigBytes = (m > 0 ? m : 1) * 64;
        const witnessSize =
          sigBytes +
          script.length +
          controlBlockLen +
          (m + 2); // length prefixes

        const leafVBytes = Math.ceil(41 + witnessSize / 4);
        if (leafVBytes > maxLeafVBytes) maxLeafVBytes = leafVBytes;
      }

      if (maxLeafVBytes > 0) return maxLeafVBytes;
      return 110; // safe fallback
    }



    // Final fallbacks
    if (input.witnessUtxo) {
      return input.redeemScript ? 91 : 68;
    }
    if (input.nonWitnessUtxo) return 148;
    return 68;
  }
}